
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { manufacturers } from '../../mocks/manufacturers';
import { Navbar } from '../platform/components/Navbar';
import { Footer } from '../platform/components/Footer';
import RegistrationModal from './components/RegistrationModal';

const ManufacturersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('전체');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [sortBy, setSortBy] = useState('rating');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const regions = [
    '전체',
    '부산',
    '인천',
    '울산',
    '경남',
    '경북',
    '전남',
    '전북',
    '충남',
    '강원',
    '제주',
  ];
  const categories = [
    '전체',
    '어구류 전문',
    '갑판용품 전문',
    '기관물품 전문',
  ];

  const filteredManufacturers = useMemo(() => {
    let filtered = manufacturers.filter((manu) => {
      const matchesSearch =
        manu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        manu.description.includes(searchTerm) ||
        manu.specialties.some((s) => s.includes(searchTerm));
      const matchesRegion =
        selectedRegion === '전체' || manu.region.includes(selectedRegion);
      const matchesCategory =
        selectedCategory === '전체' || manu.category === selectedCategory;

      return matchesSearch && matchesRegion && matchesCategory;
    });

    if (sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'reviews') {
      filtered.sort((a, b) => b.reviewCount - a.reviewCount);
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    return filtered;
  }, [searchTerm, selectedRegion, selectedCategory, sortBy]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/40 via-orange-50/20 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3cd51a8f23ba82cda65c9e94e66577e8.jpg"
            alt="석양 항구 제조업체"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6">
            <i className="ri-building-2-line text-amber-300"></i>
            <span className="text-sm text-amber-100 font-medium">
              만선허브 제조업체
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            전국 제조업체 찾기
          </h1>
          <p className="text-xl text-amber-100/90 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            부산부터 제주까지, 전국 각지의 우수한 어선용품 제조업체를 만나보세요.
            <br />
            지역별 특화 제품과 검증된 품질로 귀하의 선박을 지원합니다.
          </p>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="bg-white/80 backdrop-blur-md border-b border-amber-200/50 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-amber-400 text-xl"></i>
              <input
                type="text"
                placeholder="제조업체명, 제품, 지역으로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-amber-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4">
              {/* Region Filter */}
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-amber-600 text-lg"></i>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="px-4 py-2 border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer bg-white"
                >
                  {regions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <i className="ri-filter-3-line text-amber-600 text-lg"></i>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer bg-white"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-amber-700">정렬:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer bg-white"
              >
                <option value="rating">평점 높은순</option>
                <option value="reviews">리뷰 많은순</option>
                <option value="name">이름순</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-amber-800/70">
            총{' '}
            <strong className="text-amber-600">{filteredManufacturers.length}</strong>
            개 제조업체
          </div>
        </div>
      </section>

      {/* Manufacturers Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {filteredManufacturers.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-search-line text-6xl text-amber-200 mb-4"></i>
              <p className="text-xl text-amber-800/60">검색 결과가 없습니다.</p>
              <p className="text-amber-600/50 mt-2">
                다른 검색어나 필터를 시도해보세요.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredManufacturers.map((manu) => (
                <div
                  key={manu.id}
                  className="bg-white border border-amber-100 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300 group"
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={manu.image}
                      alt={manu.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-amber-700">
                      {manu.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Header */}
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {manu.name}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <i className="ri-map-pin-2-fill text-amber-500"></i>
                        <span className="text-sm">{manu.region}</span>
                        <span className="text-amber-300">•</span>
                        <span className="text-sm">설립 {manu.established}년</span>
                      </div>

                      {/* Rating */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`${
                                i < Math.floor(manu.rating)
                                  ? 'ri-star-fill text-amber-400'
                                  : 'ri-star-line text-amber-200'
                              } text-lg`}
                            ></i>
                          ))}
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          {manu.rating}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({manu.reviewCount})
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {manu.description}
                    </p>

                    {/* Specialties */}
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        주력 제품
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {manu.specialties.slice(0, 4).map((spec, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-100"
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-amber-100/60">
                      <div>
                        <div className="text-xs text-gray-500">직원 수</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {manu.employees}명
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">연 생산액</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {manu.annualProduction}
                        </div>
                      </div>
                    </div>

                    {/* Certifications */}
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        인증 현황
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {manu.certifications.map((cert, idx) => (
                          <span
                            key={idx}
                            className="flex items-center gap-1 text-xs text-gray-600"
                          >
                            <i className="ri-checkbox-circle-fill text-amber-500"></i>
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Contact Button */}
                    <Link
                      to={`/manufacturers/${manu.id}`}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white py-3 rounded-xl font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 group shadow-md shadow-amber-500/30"
                    >
                      <span>상세 정보 보기</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dramatic-golden-hour-sunset-over-industrial-harbor-with-warm-amber-orange-tones-silhouette-of-ships-and-cranes-against-glowing-sky-peaceful-maritime-scene-professional-photography-wide-angle&width=1920&height=500&seq=manufacturers-cta-sunset-v2&orientation=landscape"
            alt="석양 항구"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white drop-shadow-lg">
            제조업체 입점 문의
          </h2>
          <p className="text-xl text-amber-100/90 mb-8 leading-relaxed drop-shadow-md">
            우수한 제품을 생산하시나요?
            <br />
            전국의 선장님, 선주님들과 직접 연결되는 기회를 잡으세요.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-8 py-4 rounded-xl font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer shadow-lg shadow-amber-500/30"
            >
              <i className="ri-store-2-line text-xl"></i>
              <span>입점 신청하기</span>
            </button>
            <button className="bg-white/15 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/25 transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer">
              <i className="ri-phone-line text-xl"></i>
              <span>전화 문의</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default ManufacturersPage;
